import { nullableStr, oneOf, record, records, str } from '@/features/brandHub/fields';

export interface ProductImage {
  objectName: string;
  contentType: string;
  sourceUrl: string;
}

export const VERSION_KINDS = ['manual', 'import'] as const;
export type VersionKind = (typeof VERSION_KINDS)[number];

/** Who wrote one version of a product, when, and what they wrote it with. */
export interface VersionMetadata {
  kind: VersionKind;
  actor: string;
  changedAt: string | null;
  importId: string | null;
  fileName: string | null;
}

/** One field one version changed, and what it held before. */
export interface FieldChange {
  field: string;
  previous: unknown;
  current: unknown;
}

/** The whole content of one product at one point, and what changed to produce it. */
export interface ProductVersion {
  id: string;
  name: string;
  description: string;
  dimension: string;
  price: string;
  category: string;
  productImages: ProductImage[];
  moodImages: ProductImage[];
  changes: FieldChange[];
  metadata: VersionMetadata;
}

/**
 * The document id is `sku_market`, lowercased, or the bare sku when it names no market.
 * Everything the product says lives in its versions, and `current` is the one it presents.
 */
export interface Product {
  id: string;
  sku: string;
  market: string;
  brand: string;
  currentVersionId: string;
  /** Oldest first. A restore moves the pointer, so the last is not always the current. */
  versions: ProductVersion[];
  current: ProductVersion;
}

function images(value: unknown): ProductImage[] {
  return records(value)
    .filter((item) => typeof item.object_name === 'string' && item.object_name)
    .map((item) => ({
      objectName: str(item.object_name),
      contentType: str(item.content_type),
      sourceUrl: str(item.source_url),
    }));
}

/** The id a product is stored under. One function, so a write and a read agree. */
export function toProductId(sku: string, market: string): string {
  return market ? `${sku}_${market.toLowerCase()}` : sku;
}

/** The number one version id carries, so versions order by age rather than by name. */
export function versionNumber(versionId: string): number {
  const digits = versionId.replace(/^v/, '');
  return /^\d+$/.test(digits) ? Number(digits) : 0;
}

function metadata(value: unknown): VersionMetadata {
  const raw = record(value);
  return {
    kind: oneOf(VERSION_KINDS, raw.kind, 'manual'),
    actor: str(raw.actor),
    changedAt: nullableStr(raw.changed_at),
    importId: nullableStr(raw.import_id),
    fileName: nullableStr(raw.file_name),
  };
}

function changes(value: unknown): FieldChange[] {
  return records(value).map((raw) => ({
    field: str(raw.field),
    previous: raw.previous,
    current: raw.current,
  }));
}

/** Reads one version off a product, named by the key it is stored under. */
export function parseProductVersion(versionId: string, raw: Record<string, unknown>): ProductVersion {
  return {
    id: versionId,
    name: str(raw.name),
    description: str(raw.description),
    dimension: str(raw.dimension),
    price: str(raw.price),
    category: str(raw.category),
    productImages: images(raw.product_images),
    moodImages: images(raw.mood_images),
    changes: changes(raw.changes),
    metadata: metadata(raw.metadata),
  };
}

function versions(value: unknown): ProductVersion[] {
  return Object.entries(record(value))
    .map(([versionId, raw]) => parseProductVersion(versionId, record(raw)))
    .sort((one, other) => versionNumber(one.id) - versionNumber(other.id));
}

/** A product with no version, or naming one it does not hold, is dropped rather than shown. */
export function parseProduct(documentId: string, raw: Record<string, unknown>): Product | null {
  const history = versions(raw.versions);
  const currentVersionId = str(raw.current_version_id);
  const current = history.find((one) => one.id === currentVersionId);
  if (!documentId || !current || !current.name) return null;
  return {
    id: documentId,
    sku: str(raw.sku) || documentId,
    market: str(raw.market).toUpperCase(),
    brand: str(raw.brand),
    currentVersionId,
    versions: history,
    current,
  };
}
